import { createAsyncThunk } from '@reduxjs/toolkit';
import debug from '../utils/debug';
import { OrderingOperations, OrderingSelectors, OrderingConstants } from 'polygon-ordering';
import { setCurrentModal } from './setCurrentModal';
import type { RootState } from 'store';

// Modal IDs:
import { ORDER_TYPE_MODAL_ID } from '../modals/OrderTypeModal';
import { DELIVERY_DETAILS_MODAL_ID } from '../modals/DeliveryDetailsModal';
import { LOCATION_MODAL_ID } from '../modals/LocationModal';
import { CATERING_DETAILS_MODAL_ID } from '../modals/CateringDetailsModal';
import { adjustOrder } from './adjustOrder';
import { saleTypeSelected } from '../utils/analytics';
import { updateLoadingDependencies } from '../slices/loadingDependencies';
import history from '../history';
import { SUB_SALETYPE_SCREEN_ROUTE } from '../hooks/useRoutes';

const { getEnabledSaleTypes, getLocation } = OrderingSelectors;
const { setPickupTime, setBufferPickupTime } = OrderingOperations;
const { SALE_TYPE, DELIVERY_SALE_TYPES } = OrderingConstants;

export const adjustSaleType = createAsyncThunk(
  'adjustSaleType',
  async (
    data: {
      saleType?: number;
      locationId?: string | null;
      isChild?: boolean;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    const { saleType, locationId, isChild } = data;
    debug('adjusting sale type', { saleType });

    const isDeliverySaleType = saleType && DELIVERY_SALE_TYPES.includes(saleType);

    try {
      const enabledSaleTypes = getEnabledSaleTypes(getState() as RootState);
      const location = getLocation(getState() as RootState);

      const modalParams = (getState() as RootState).modalParams;
      const currentModalId = (getState() as RootState).currentModalId;

      const previousModalId = enabledSaleTypes.length > 1 ? ORDER_TYPE_MODAL_ID : null;

      dispatch(setPickupTime(null));
      dispatch(setBufferPickupTime(null));

      const initialCateringDeliveryTypeAvailable =
        saleType === SALE_TYPE.CATERING &&
        enabledSaleTypes.includes(SALE_TYPE.CATERING_DELIVERY) &&
        !isChild;

      if (locationId) {
        if (isDeliverySaleType) {
          dispatch(
            setCurrentModal({
              modalId: DELIVERY_DETAILS_MODAL_ID,
              previousModalId,
              params: { ...modalParams, saleType },
            }),
          );
        } else {
          dispatch(updateLoadingDependencies({ adjustSaleTypeInProgress: true }));

          if (saleType === SALE_TYPE.TABLE_ORDER || saleType === SALE_TYPE.DINE_IN) {
            dispatch(
              setCurrentModal({
                modalId: LOCATION_MODAL_ID,
                params: {
                  ...modalParams,
                  saleType,
                  locationId,
                  action: async () => {
                    // Putting this in the callback so the analytics logging isn't triggered optimistically
                    saleTypeSelected({ saleType: saleType!, store: location?.name });
                    await dispatch(
                      adjustOrder({
                        saleType,
                        locationId,
                      }),
                    );
                  },
                },
              }),
            );
          } else if (initialCateringDeliveryTypeAvailable) {
            // If delivery of catering saletype is included in enabled saletypes
            // have it show the order picker screen for catering
            dispatch(
              setCurrentModal({
                modalId: ORDER_TYPE_MODAL_ID,
                params: {
                  saleType: SALE_TYPE.CATERING,
                },
              }),
            );
          } else {
            saleTypeSelected({ saleType: saleType!, store: location?.name });

            if (saleType === SALE_TYPE.CATERING) {
              dispatch(
                setCurrentModal({
                  modalId: CATERING_DETAILS_MODAL_ID,
                  previousModalId,
                  params: { ...modalParams, saleType },
                }),
              );
            } else {
              await dispatch(
                adjustOrder({
                  saleType,
                  locationId,
                }),
              );
            }
          }

          dispatch(updateLoadingDependencies({ adjustSaleTypeInProgress: false }));
        }
      } else {
        if (initialCateringDeliveryTypeAvailable) {
          if (currentModalId) {
            dispatch(
              setCurrentModal({
                modalId: ORDER_TYPE_MODAL_ID,
                params: {
                  saleType: SALE_TYPE.CATERING,
                },
              }),
            );
          } else {
            // If delivery of catering saletype is included in enabled saletypes
            // have it navigate to PickSaleTypeChild screen
            history.push(SUB_SALETYPE_SCREEN_ROUTE, { saleType: SALE_TYPE.CATERING });
          }
        } else {
          let modalId = LOCATION_MODAL_ID;

          if (isDeliverySaleType) {
            modalId = DELIVERY_DETAILS_MODAL_ID;
          } else {
            // Putting this analytics logging here because we don't want to track for deliveries. The delivery modal tracks this separately and would therefore cause a duplicate.
            saleTypeSelected({ saleType: saleType!, store: location?.name });
            switch (saleType) {
              case SALE_TYPE.CATERING:
                modalId = CATERING_DETAILS_MODAL_ID;
                break;

              default:
                break;
            }
          }

          dispatch(
            setCurrentModal({
              modalId,
              params: { ...modalParams, saleType },
              previousModalId: null,
            }),
          );
        }
      }
    } catch (e) {
      dispatch(updateLoadingDependencies({ adjustSaleTypeInProgress: false }));
      return rejectWithValue(e);
    }
  },
);
